import { Group, Stack } from "@mantine/core";
import Heading from "@/components/atoms/Heading";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useGetCompanyById } from "@/services/Company";
import {
  useGetOpeningHours,
  useGetHolidays,
  useGetSlotConfig,
  useGetFractionWindows,
} from "@/services/ReceptionSettings";
import OpeningHoursSection from "@/components/organisms/ReceptionSettings/OpeningHoursSection";
import TimeSlotsSection from "@/components/organisms/ReceptionSettings/TimeSlotsSection";
import FractionWindowsSection from "@/components/organisms/ReceptionSettings/FractionWindowsSection";
import ReceptionCalendarModal from "@/components/organisms/ReceptionSettings/ReceptionCalendarModal";
import GenerateSlotsModal from "@/components/organisms/ReceptionSettings/GenerateSlotsModal";
import CTA from "@/components/atoms/CTA";
import { useState } from "react";

export default function ReceptionSettings() {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [generateSlotsOpen, setGenerateSlotsOpen] = useState(false);

  const companyId = currentCompany?.companyId as number;

  const { data: company } = useGetCompanyById(companyId, { enabled: !!companyId });
  const isEopEnabled = company?.isOrderPlanningEnabled ?? false;

  const { data: openingHours = [], isLoading: loadingHours } = useGetOpeningHours(companyId, {
    enabled: isEopEnabled && !!companyId,
  });
  const { data: holidays = [], isLoading: loadingHolidays } = useGetHolidays(companyId, {
    enabled: isEopEnabled && !!companyId,
  });
  const { data: slotConfig, isLoading: loadingSlotConfig } = useGetSlotConfig(companyId, {
    enabled: isEopEnabled && !!companyId,
  });
  const { data: fractionWindows = [], isLoading: loadingWindows } = useGetFractionWindows(companyId, {
    enabled: isEopEnabled && !!companyId,
  });

  return (
    <Stack data-testid="reception-settings" gap="xl">
      <Group justify="space-between" align="center">
        <Heading order={2} styleOrder={4} tt="uppercase">
          {t("settings:reception.title")}
        </Heading>
        <Group gap="sm">
          <CTA
            intent="secondary"
            size="sm"
            onClick={() => setGenerateSlotsOpen(true)}
            disabled={!isEopEnabled}
            data-testid="generate-slots-cta"
          >
            {t("settings:reception.timeSlots.generateSlots")}
          </CTA>
          <CTA intent="secondary" size="sm" onClick={() => setCalendarOpen(true)}>
            {t("settings:reception.timeSlots.viewCalendar")}
          </CTA>
        </Group>
      </Group>
      {isEopEnabled && (
        <>
          <FractionWindowsSection companyId={companyId} fractionWindows={fractionWindows} isLoading={loadingWindows} />
          <OpeningHoursSection
            companyId={companyId}
            openingHours={openingHours}
            holidays={holidays}
            isLoading={loadingHours || loadingHolidays}
          />

          <TimeSlotsSection
            companyId={companyId}
            slotConfig={slotConfig ?? null}
            openingHours={openingHours}
            receiverCompanyId={companyId}
            isLoading={loadingSlotConfig}
          />
        </>
      )}
      <ReceptionCalendarModal
        opened={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        receiverCompanyId={companyId}
      />
      <GenerateSlotsModal
        opened={generateSlotsOpen}
        companyId={companyId}
        onClose={() => setGenerateSlotsOpen(false)}
      />
    </Stack>
  );
}
